import Text from '@creator/ui/components/Text/Text';
import { convertFirebaseTimestampToDate, formatDate, toCustomLocaleDate, toCustomLocaleDateHour, toCustomLocaleDateYearMonthDay } from '@creator/ui/utils/date-utils';
import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import { cn } from '@creator/ui/utils/ui';
import Link from '@src/basic-components/Link/Link';
import UserDisplayNameWithLink from '@src/basic-components/UserDisplayName/UserDisplayNameWithLink';
import ProfileImage, { type ProfileImageProps } from '@src/components/ProfileImage/ProfileImage';
import TokenImage, { type TokenImageProps } from '@src/components/Token/TokenImage/TokenImage';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import type { Timestamp } from 'firebase/firestore/lite';
import type { FC } from 'react';
import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export interface PostBasicInfoProps {
    className?: string;
    createdAt?: string | Timestamp;
    updatedAt?: string | Timestamp;
    publisherName?: string;
    publisherProfileName?: string;
    publisherSlugUrl?: string;
    tokenName: string;
    showTokenImage?: boolean;
    communityName?: string;
    profileImageProps?: Partial<ProfileImageProps>;
    tokenImageProps?: Partial<TokenImageProps>;
    profileBackLinkUrl?: string;
    profileBackLinkLabel?: string;
    showFullCreatedAtDate?: boolean;
    showPartnerLabel?: boolean;
    publisherBio?: string;
}

const PostBasicInfo: FC<PostBasicInfoProps> = props => {
    const {
        showTokenImage = false,
        profileBackLinkUrl = '',
        publisherSlugUrl = '',
        profileBackLinkLabel = '',
        profileImageProps = {},
        communityName = '',
        createdAt,
        tokenName,
        publisherName = '',
        showPartnerLabel = false,
    } = props;

    const { t } = useTranslation();
    const navigate = useNavigate();

    function getBaseClassName() {
        const { className } = props;
        const baseClassName = 'flex items-center text-gray-900 dark:text-gray-50 text-sm';
        return cn(baseClassName, className);
    }
    function getPostCreatedTime() {
        const { showFullCreatedAtDate = false } = props;
        if (!createdAt) return '';

        const _date = convertFirebaseTimestampToDate(createdAt);
        if (showFullCreatedAtDate) return toCustomLocaleDate(_date);
        return formatDate(_date);
    }

    function getPostUpdatedTime() {
        const { updatedAt } = props;
        if (!updatedAt) return '';

        const _date = convertFirebaseTimestampToDate(updatedAt);
        return toCustomLocaleDate(_date);
    }

    function renderUpdatedDate() {
        const { updatedAt } = props;
        if (!updatedAt || !createdAt) return null;

        const createdDate = convertFirebaseTimestampToDate(createdAt);
        const updatedDate = convertFirebaseTimestampToDate(updatedAt);

        // Only show updated date if it's significantly different from created date (more than 1 minute)
        const isSignificantlyDifferent = Math.abs(updatedDate.getTime() - createdDate.getTime()) > 60 * 1000;
        if (!isSignificantlyDifferent) return null;

        return (
            <Fragment>
                <div className="w-[1px] bg-gray-950 dark:bg-gray-50 h-[1em] shrink-0" />
                <Text data-ssr="post-updated-date">{t('postBasicInfo.lastUpdatedAt', { date: getPostUpdatedTime() })}</Text>
            </Fragment>
        );
    }

    function renderDisplayNameProfileImage(displayName = '', className = '') {
        const _props = mergeProps(
            {
                tokenName,
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/${ensureTokenUrl(tokenName)}/author/${publisherSlugUrl || publisherName}?backLinkUrl=${profileBackLinkUrl}&backLinkLabel=${profileBackLinkLabel}`);
                },
                className: className,
                displayName,
            },
            profileImageProps
        );
        return <ProfileImage {..._props} />;
    }

    function renderTokenImage(className = '') {
        const { tokenImageProps = {} } = props;
        const _props = mergeProps(
            {
                tokenName,
                onClick: () => navigate(`/${ensureTokenUrl(tokenName)}`),
                className,
            },
            tokenImageProps
        );

        return <TokenImage {..._props} />;
    }

    function renderTokenInfo() {
        if (!publisherName && communityName) return renderTokenImage('mr-3 w-7 h-7');
        if (showTokenImage) {
            return (
                <div className="flex items-end mr-3 shrink-0">
                    {renderTokenImage('w-13 h-13')}
                    {publisherName && renderDisplayNameProfileImage(publisherName, '-ml-3 w-7 h-7')}
                </div>
            );
        }
        return <div className="flex items-end mr-3 shrink-0">{publisherName && renderDisplayNameProfileImage(publisherName, 'w-11 h-11')}</div>;
    }

    function renderPublisher() {
        const { publisherName = '', publisherProfileName } = props;
        return (
            <UserDisplayNameWithLink
                slugUrl={publisherSlugUrl}
                tokenName={tokenName}
                profileName={publisherProfileName}
                displayName={publisherName}
                userDisplayNameProps={{ className: 'font-bold' }}
            />
        );
    }

    function renderTime() {
        if (!createdAt) return;
        return (
            <Fragment>
                <div className="w-[1px] bg-gray-950 dark:bg-gray-50 h-[1em] shrink-0" />
                <Text data-ssr="post-date">{getPostCreatedTime()}</Text>
                {renderUpdatedDate()}
            </Fragment>
        );
    }

    function renderPatnerLabel() {
        if (!showPartnerLabel) return;

        return (
            <Fragment>
                <div className="w-[1px] bg-gray-950 dark:bg-gray-50 h-[1em] shrink-0" />
                <Link to={`/${ensureTokenUrl(tokenName)}/tab/holders?type=partner`} className="">
                    <Text>{t('postBasicInfo.partnerLabel')}</Text>
                </Link>
            </Fragment>
        );
    }

    function renderBio() {
        const { publisherBio } = props;
        if (!publisherBio) return;

        return <Text className="text-xs line-clamp-1">{publisherBio}</Text>;
    }

    function renderContent() {
        return (
            <div className="">
                <div className="relative flex flex-wrap items-center space-x-2">
                    {renderPublisher()}
                    {renderTime()}
                    {renderPatnerLabel()}
                </div>
                {renderBio()}
            </div>
        );
    }

    return (
        <div className={getBaseClassName()}>
            {renderTokenInfo()}
            {renderContent()}
        </div>
    );
};

export default memo(PostBasicInfo, propsEqualityFn);
